import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
	C4ListResponse,
	CarrierNotes,
	CountMap,
	IS_EMPTY,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	Owner,
	ProjectDTO,
	QUICK_SEARCH_TABLE,
	QuickSearchResult,
	RestService,
	TASK_STATUS_UUID,
	TASK_STATUS_UUID_MAP,
	TASK_TABLE,
	TaskDTO,
	TaskTable,
	User,
	USERS_LIST_FOR_POPOVER,
} from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { HighlightTextPipe } from '@shared/highlight-text.pipe';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { AccessCacheService } from '@service/access-cache/access-cache.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { ACCOUNT_STATUS, PLAN_PRODUCTS } from '@domain/constants/app-variables';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { QuickNotesViewDialog } from '@notes/quick-notes-view-dialog/quick-notes-view-dialog';
import { NoteType } from '@notes/note-list/note-list.component';
import { Store } from '@ngrx/store';
import { ERROR_HANDLING } from '../../../store/actions/error-handling.actions';

@Component({
	selector: 'app-project-search-results',
	templateUrl: './project-search-results.component.html',
	styleUrls: ['./project-search-results.component.scss'],
	providers: [HighlightTextPipe],
})
export class ProjectSearchResultsComponent implements OnInit {
	@Input() projectSearchString: string = '';
	@Input() pendingHTTPRequests: Subject<void> = new Subject<void>();
	@Output() closePopover: EventEmitter<null> = new EventEmitter();

	isLoadingProjectsSearch: boolean = false;
	projectsList: Array<QuickSearchResult> = null;
	projectDTOList: Array<ProjectDTO> = null;
	accountUsers: User[] = [];
	projectUsersListMap: { [projectId: string]: Owner[] } = {};
	notesCountMap: CountMap = null;
	carriersCountMap: CountMap = null;
	isLoadingTasks: boolean = false;
	allTasksList: Array<TaskTable> = null;
	isAdminOrOwner: boolean = false;
	IS_TASKS_ENABLED: boolean = false;
	recentlyViewedProjects: Array<ProjectDTO> = null;

	IS_EMPTY = IS_EMPTY;
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	IS_NULL_OR_UNDEFINED = IS_NULL_OR_UNDEFINED;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	USERS_LIST_FOR_POPOVER = USERS_LIST_FOR_POPOVER;
	SegmentEventsConstants = SegmentEventsConstants;

	constructor(
		public utilService: UtilService,
		public cargochief: Cargochief,
		private urlService: UrlService,
		private restService: RestService,
		private accessCache: AccessCacheService,
		private projectHelperService: ProjectHelperService,
		private segmentWrapper: SegmentWrapperService,
		private dialog: MatDialog,
		private router: Router,
		private store: Store
	) {
		this.accountUsers = this.accessCache.getAccountUsers();
	}

	ngOnInit(): void {
		this.populateProjectUsersListMap();
		this.isAdminOrOwner = this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner();
		this.IS_TASKS_ENABLED = this.cargochief.isProductEnabled(PLAN_PRODUCTS.TASKS);
	}

	initiateSearch() {
		this.isLoadingProjectsSearch = false;
		if (IS_NOT_EMPTY(this.projectSearchString)) {
			this.searchProjects();
		} else if (
			IS_EMPTY(this.projectSearchString) &&
			!this.utilService.isNullOrUndefined(this.recentlyViewedProjects) &&
			this.recentlyViewedProjects.length > 0
		) {
			this.notesCountMap = null;
			this.carriersCountMap = null;
			this.allTasksList = null;
			this.projectsList = null;
			this.projectDTOList = null;
			this.copyRecentlyViewedProjects();
		}
	}

	copyRecentlyViewedProjects() {
		this.projectsList = [];
		this.projectDTOList = [];
		if (IS_NOT_NULL_OR_UNDEFINED(this.recentlyViewedProjects)) {
			this.projectDTOList = [...this.recentlyViewedProjects];
			this.projectsList = [...QUICK_SEARCH_TABLE(this.recentlyViewedProjects)];
			this.populateMetadata();
		}
	}

	fetchRecentlyViewedProjects() {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.recentlyViewedProjects) &&
			this.recentlyViewedProjects.length > 0
		) {
			this.copyRecentlyViewedProjects();
		} else {
			this.searchProjects({ firstSearch: true });
		}
	}

	searchProjects({ firstSearch }: { firstSearch: boolean } = { firstSearch: false }) {
		this.projectsList = null;
		this.isLoadingProjectsSearch = true;
		const url = this.urlService.searchProjects(this.projectSearchString, 0, 5);
		this.restService
			.GET(url)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response) => {
					this.projectDTOList = response.content;
					this.projectsList = QUICK_SEARCH_TABLE(this.projectDTOList, this.accountUsers);
					if (firstSearch) {
						this.recentlyViewedProjects = this.projectDTOList;
					}
					this.populateMetadata();
					this.isLoadingProjectsSearch = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to search projects. Please try again after reloading the page.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.projectsList = [];
					this.isLoadingProjectsSearch = false;
				}
			);
	}

	changeOwner(user: Owner, index: number) {
		const project = this.projectDTOList[index];
		project.assigneeId = !this.utilService.isNullOrUndefined(user) ? user.userId : null;
		const updateMsg: string = 'Project owner updated successfully.',
			projectDTO: ProjectDTO = Object.assign({}, project);
		const isOwnerAssigned = IS_NOT_NULL_OR_UNDEFINED(projectDTO.assigneeId);
		const url = this.urlService.addProject() + `${isOwnerAssigned ? '?&isOwnerUpdated=true' : ''}`;
		this.restService.PUT(url, projectDTO).subscribe(
			(result: any) => {
				const updatedProjectDTO = this.projectHelperService.convertProjectCentsToDollars(
					result.data
				);
				this.utilService.showSnackbar(updateMsg, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				this.projectDTOList[index] = updatedProjectDTO;
				this.projectsList = QUICK_SEARCH_TABLE(this.projectDTOList, this.accountUsers);
			},
			(error) => {
				this.store.dispatch(
					ERROR_HANDLING.HANDLE_PROJECT_UPDATE_ERROR({
						error,
						forAdd: false,
						defaultErrorMessage: 'Unable to update project now. Please try again after reloading.',
					})
				);
			}
		);
		const payload = {
			owner: user,
			user: this.utilService.getUserDataForSegmentEvent(),
			projectDTO: projectDTO,
		};
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_assigned_project, payload);
	}

	populateProjectUsersListMap() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.projectsList) && this.projectsList.length > 0) {
			this.projectsList?.forEach((project) => {
				this.projectUsersListMap[project.id] = USERS_LIST_FOR_POPOVER({
					users: this.accountUsers,
					assigneeId: project.assigneeId,
					removeHavingStatus: ACCOUNT_STATUS.INACTIVE,
				});
			});
		}
	}

	populateMetadata() {
		if (!this.utilService.isNullOrUndefined(this.projectsList) && this.projectsList.length > 0) {
			this.getProjectNotesCount();
			this.getAllTasks();
			this.getCarriersCount();
		}
	}

	getProjectNotesCount() {
		this.notesCountMap = null;
		const projectIds = this.projectsList.map((project: QuickSearchResult) => project.id);
		const url = this.urlService.getProjectNotesCount();
		const payload = {
			projectIds: projectIds,
		};
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response) => {
					if (
						!this.utilService.isNullOrUndefined(response.data) &&
						!this.utilService.isNullOrUndefined(response.data.countMap)
					) {
						this.notesCountMap = response.data.countMap;
						this.updateNotesCount();
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get project notes count.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	updateNotesCount() {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.notesCountMap) &&
			IS_NOT_NULL_OR_UNDEFINED(this.projectsList)
		) {
			setTimeout(() => {
				this.projectsList?.map((project: QuickSearchResult) => {
					project.notesCount = this.notesCountMap[project.id];
				});
			}, 0);
		}
	}

	notesAdded(notes: CarrierNotes) {
		this.notesCountMap[notes.projectId] += 1;
		this.updateNotesCount();
	}

	getAllTasks() {
		this.isLoadingTasks = true;
		this.allTasksList = null;
		const projectIds = this.projectsList.map((project: QuickSearchResult) => project.id);
		const url = this.urlService.listTaskByIds();
		const payload = {
			projectIds: projectIds,
			accountId: this.cargochief.ACCOUNT_ID,
			statusIds: [TASK_STATUS_UUID_MAP.TO_DO, TASK_STATUS_UUID_MAP.IN_PROGRESS],
			assigneeId: !this.isAdminOrOwner ? this.cargochief.USER_ID : undefined,
		};
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response: C4ListResponse<TaskDTO>) => {
					this.allTasksList = TASK_TABLE(response.data);
					this.isLoadingTasks = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get tasks list',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isLoadingTasks = false;
				}
			);
	}

	getTasksForProject(projectId: string): Array<TaskTable> {
		if (!this.utilService.isNullOrUndefined(this.allTasksList)) {
			return this.allTasksList.filter((task) => task.projectId == projectId);
		}
	}

	tasksUpdated(updatedTasks: Array<TaskTable>) {
		const doneStatus = this.getDoneStatusString();
		this.allTasksList = this.allTasksList
			.map((task) => {
				const filteredTask = updatedTasks.find((updatedTask) => updatedTask.id == task.id);
				if (filteredTask) {
					return filteredTask;
				}
				return task;
			})
			.filter((task) => task.status !== doneStatus);
	}

	getDoneStatusString(): string {
		return TASK_STATUS_UUID[TASK_STATUS_UUID_MAP.DONE];
	}

	taskAdded(newTask: TaskTable) {
		this.allTasksList = [newTask, ...this.allTasksList];
	}

	getCarriersCount() {
		this.carriersCountMap = null;
		const projectIds = this.projectsList.map((project: QuickSearchResult) => project.id);
		const url = this.urlService.getCarriersCount();
		const payload = {
			projectIds: projectIds,
		};
		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.pendingHTTPRequests))
			.subscribe(
				(response) => {
					if (
						!this.utilService.isNullOrUndefined(response.data) &&
						!this.utilService.isNullOrUndefined(response.data.countMap)
					) {
						this.carriersCountMap = response.data.countMap;
						this.updateCarriersCount();
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to get carriers count.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	updateCarriersCount() {
		if (
			IS_NOT_NULL_OR_UNDEFINED(this.carriersCountMap) &&
			IS_NOT_NULL_OR_UNDEFINED(this.projectsList)
		) {
			setTimeout(() => {
				this.projectsList?.map((project: QuickSearchResult) => {
					project.carriersCount = this.carriersCountMap[project.id];
				});
			}, 0);
		}
	}

	showCarriers(projectId: string) {
		this.closePopover.emit();
		this.router.navigate(['carriers'], {
			queryParams: {
				inNetwork: false,
				projectIds: projectId,
				page: 0,
			},
		});
	}

	showProject(projectId: string) {
		this.closePopover.emit();
		this.router.navigate([`projects/project_details/${projectId}`]);
	}

	showNotes(project: QuickSearchResult) {
		const dialogRef = this.dialog.open(QuickNotesViewDialog, {
			width: '500px',
			data: {
				carrierId: null,
				truckLaneId: null,
				projectId: project.id,
				name: project.name,
				noteType: NoteType.PROJECT,
			},
		});
		dialogRef.componentInstance.notesDialogClosed.subscribe((data) => {
			this.projectsList.map((project: QuickSearchResult) => {
				if (project.id == data.projectId) {
					project.notesCount = data.count;
				}
			});
		});
	}
}
